import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CustomerProvider } from "@/context/CustomerContext";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CustomerOnboarding from "@/pages/CustomerOnboarding";
import PendingApproval from "@/pages/PendingApproval";
import DailyUpdates from "@/pages/customer/DailyUpdates";
import CustomerDatabase from "@/pages/customer/Database";
import CustomerDataExport from "@/pages/customer/DataExport";
import MyPackage from "@/pages/customer/MyPackage";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUploads from "@/pages/admin/Uploads";
import AdminApprovals from "@/pages/admin/Approvals";
import AdminCustomers from "@/pages/admin/Customers";
import AdminDataDownload from "@/pages/admin/DataDownload";
import AdminSampleRequests from "@/pages/admin/SampleRequests";

// Public marketing / legal pages
import McaDatabase from "@/pages/public/McaDatabase";
import GstDatabase from "@/pages/public/GstDatabase";
import NewCompanyDatabase from "@/pages/public/NewCompanyDatabase";
import McaGstDatabase from "@/pages/public/McaGstDatabase";
import Pricing from "@/pages/public/Pricing";
import SampleData from "@/pages/public/SampleData";
import Contact from "@/pages/public/Contact";
import About from "@/pages/public/About";
import WhyRegistryFeed from "@/pages/public/WhyRegistryFeed";
import HowItWorks from "@/pages/public/HowItWorks";
import DataCoverage from "@/pages/public/DataCoverage";
import Faq from "@/pages/public/Faq";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Refund from "@/pages/public/Refund";
import Disclaimer from "@/pages/public/Disclaimer";
import AcceptableUse from "@/pages/public/AcceptableUse";
import CookiePolicy from "@/pages/public/CookiePolicy";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="app-loading">
      <div className="flex flex-col items-center gap-5">
        <div className="relative grid place-items-center">
          <div className="absolute h-24 w-24 rounded-3xl bg-primary/20 loader-ring" />
          <div className="absolute h-24 w-24 rounded-3xl bg-primary/20 loader-ring loader-ring-delay" />
          <img src="/pwa-icon-192.png" alt="RegistryFeed" className="relative h-20 w-20 rounded-3xl shadow-lg shadow-primary/30 loader-logo-pulse" />
        </div>
        <div className="font-heading font-bold text-lg loader-shimmer-text">RegistryFeed</div>
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-primary loader-dot" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary loader-dot" style={{ animationDelay: "0.15s" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-primary loader-dot" style={{ animationDelay: "0.3s" }} />
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, customerOnly = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard/daily" replace />;
  if (customerOnly && user.role !== "customer") return <Navigate to="/admin" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Landing />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard/daily"} replace />;
}

/** Layout that wraps all /dashboard/* routes with the CustomerProvider so one fetch feeds all pages. */
function CustomerLayout() {
  return (
    <CustomerProvider>
      <Outlet />
    </CustomerProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Public marketing pages (SEO-facing, always accessible) */}
            <Route path="/mca-database" element={<McaDatabase />} />
            <Route path="/gst-database" element={<GstDatabase />} />
            <Route path="/new-company-database" element={<NewCompanyDatabase />} />
            <Route path="/mca-gst-database" element={<McaGstDatabase />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/sample-data" element={<SampleData />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/why-registryfeed" element={<WhyRegistryFeed />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/data-coverage" element={<DataCoverage />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund-policy" element={<Refund />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />

            <Route element={<ProtectedRoute customerOnly><AppShell /></ProtectedRoute>}>
              <Route path="/onboarding" element={<CustomerOnboarding />} />
              <Route path="/pending" element={<PendingApproval />} />
              <Route element={<CustomerLayout />}>
                <Route path="/dashboard" element={<Navigate to="/dashboard/daily" replace />} />
                <Route path="/dashboard/daily" element={<DailyUpdates />} />
                <Route path="/dashboard/database" element={<CustomerDatabase />} />
                <Route path="/dashboard/export" element={<CustomerDataExport />} />
                <Route path="/dashboard/package" element={<MyPackage />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute adminOnly><AppShell /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/uploads" element={<AdminUploads />} />
              <Route path="/admin/approvals" element={<AdminApprovals />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/data" element={<AdminDataDownload />} />
              <Route path="/admin/samples" element={<AdminSampleRequests />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
